// Tách khỏi FilmPlanContext.tsx (261010 P2b-1) để test được dưới jest (file kia import antd). Nội dung dời nguyên văn; thêm `useFilmPlanOptional`.
import { createContext, useContext } from 'react';
import type { FilmPlan } from '../../types/film';

/**
 * Film Mode v4 — context chia sẻ "plan đang chọn" cho mọi tab (Plan/Frames/Videos/Assembly).
 * Chọn plan 1 lần ở thanh tab → mọi tab dùng chung, không phải chọn lại.
 */
export interface FilmPlanCtx {
  storyId: string;
  plans: FilmPlan[];
  loadingPlans: boolean;
  planId?: string;
  plan: FilmPlan | null;      // full plan (scenes/shots/dialogue)
  loadingPlan: boolean;
  selectPlan: (id?: string) => void;
  reloadPlan: () => void;     // refresh full plan đang chọn
  /**
   * Tải lại danh sách plan. `silent` = KHÔNG bật `loadingPlans`.
   *
   * 🔴 Cần vì màn danh sách poll 5 giây khi đang có việc nền (tạo plan): mỗi lượt bật loading là cả grid
   * nhảy sang spinner rồi vẽ lại — user thấy "trang nháy liên tục". Poll phải im lặng, chỉ lượt tải do người
   * dùng chủ động (mở màn, bấm ⟳) mới được hiện spinner.
   */
  reloadPlans: (opts?: { silent?: boolean }) => Promise<FilmPlan[]>;
}

/**
 * 261010 P8e — chọn plan tự động (F8). `current` đang có thì giữ; không thì `preferred` (kể cả khi chưa có trong `plans`);
 * không thì `plans[0]`. Chuỗi rỗng = chưa chọn. Không antd ⇒ jest chạy được.
 */
export function pickPlanId(args: {
  current?: string | null;
  preferred?: string | null;
  plans: Array<{ id: string }>;
}): string | undefined {
  const present = (s?: string | null): s is string => typeof s === 'string' && s.length > 0;
  if (present(args.current)) return args.current;
  if (present(args.preferred)) return args.preferred;
  return args.plans[0]?.id;
}

export const Ctx = createContext<FilmPlanCtx | null>(null);

export const useFilmPlan = (): FilmPlanCtx => {
  const c = useContext(Ctx);
  if (!c) throw new Error('useFilmPlan phải dùng trong <FilmPlanProvider>');
  return c;
};

/** 261010 P2b-1 — đọc context KHÔNG ném lỗi (khung mới `FilmWorkflowPage` không nằm trong `FilmPlanProvider`). Ngoài provider ⇒ `null`. */
export const useFilmPlanOptional = (): FilmPlanCtx | null => useContext(Ctx);

